import type { SourceBundle, SourceFileSnapshot } from "../../shared/codemap";
import { parseChewConfigYaml, type ChewConfig } from "../../shared/config";
import { emptyGlossary, parseGlossaryYaml, type Glossary } from "../../shared/glossary";
import type { ReviewGuide } from "../../shared/guide";
import { parseGuideYaml } from "../../shared/guide";

export interface FixtureDescriptor {
  id: string;
  repository: string;
  pullNumber: number;
  /** Exact PR head represented by this queue entry. Hosted entries always set it. */
  headSha?: string;
  title: string;
  authorLogin?: string;
  body?: string;
  url?: string;
  mode: "live" | "replay";
  scope: ReviewGuide["scope"];
  guidePath: string;
  filesPath: string;
  reviewPath?: string;
  draft?: boolean;
  reviewable?: boolean;
  assignment?: {
    reason: "requested-reviewer" | "requested-team" | "assignee" | "general-pool" | "author" | "other-reviewers-requested";
    slotKey?: string;
    slotLabel?: string;
  };
  claim?: {
    status: "available" | "owned" | "held";
    slotKey: string;
    slotLabel: string;
    reviewerLogin?: string;
    claimedAt?: string;
    lastActiveAt?: string;
    expiresAt?: string;
  };
  chewfile?: {
    status: "guided" | "description" | "unguided" | "required-missing" | "stale";
    requiredReasons: string[];
    effectiveChangedLoc: number;
    excludedChangedLoc: number;
  };
  queue: {
    estimatedMinutes: number;
    difficulty: "quick" | "normal" | "tough";
    areas: string[];
    score: number;
    reason: string;
    reviewGap: string;
    /** Authoritative server-side queue age for live PRs. */
    waitingSince?: string;
  };
  stats: {
    additions: number;
    deletions: number;
    changedFiles: number;
    commits: number;
  };
}

export interface FixtureFile extends SourceFileSnapshot {
  language: string;
  provenance: "illustrative" | "github" | "git";
  loading?: boolean;
  loadError?: string;
}

export interface FixtureFiles extends SourceBundle {
  schemaVersion: 1 | 2;
  files: Record<string, FixtureFile>;
}

export interface LoadedFixture {
  descriptor: FixtureDescriptor;
  guide: ReviewGuide;
}

export interface LoadedReviewBundle extends LoadedFixture {
  config: ChewConfig;
  glossary: Glossary;
  files: FixtureFiles;
}

const reviewBundleCache = new Map<string, Promise<LoadedReviewBundle>>();
let configCache: Promise<ChewConfig> | undefined;
let glossaryCache: Promise<Glossary> | undefined;

export class HostedAuthenticationRequired extends Error {
  constructor() {
    super("Sign in with GitHub to open CHEW.");
    this.name = "HostedAuthenticationRequired";
  }
}

async function checkedFetch(path: string): Promise<Response> {
  const response = await fetch(path);
  if (response.status === 401) throw new HostedAuthenticationRequired();
  if (!response.ok) throw new Error(`Could not load ${path} (${response.status})`);
  return response;
}

export async function loadFixtureIndex(): Promise<FixtureDescriptor[]> {
  const response = await checkedFetch("/fixtures/index.json");
  return (await response.json()) as FixtureDescriptor[];
}

export function loadChewConfig(): Promise<ChewConfig> {
  configCache ??= checkedFetch("/chewfiles/config.yaml")
    .then(async (response) => parseChewConfigYaml(await response.text()));
  return configCache;
}

/** Optional by design: a repository without a glossary still reviews normally. */
export function loadGlossary(): Promise<Glossary> {
  glossaryCache ??= fetch("/chewfiles/glossary.yaml")
    .then(async (response) => (response.ok ? parseGlossaryYaml(await response.text()) : emptyGlossary))
    .catch(() => emptyGlossary);
  return glossaryCache;
}

export async function loadFixture(descriptor: FixtureDescriptor): Promise<LoadedFixture> {
  const response = await checkedFetch(descriptor.guidePath);
  const guide = parseGuideYaml(await response.text());
  return { descriptor, guide };
}

export async function loadFixtureFiles(
  descriptor: FixtureDescriptor,
  expectedSource: ReviewGuide["source"],
): Promise<FixtureFiles> {
  const response = await checkedFetch(descriptor.filesPath);
  const files = (await response.json()) as FixtureFiles;
  if (
    files.source.baseSha !== expectedSource.baseSha ||
    files.source.headSha !== expectedSource.headSha
  ) {
    throw new Error("The cached code does not match the guide's pinned commits");
  }
  return files;
}

export function loadReviewBundle(descriptor: FixtureDescriptor): Promise<LoadedReviewBundle> {
  const cached = reviewBundleCache.get(descriptor.id);
  if (cached) return cached;
  const loading = loadFixture(descriptor)
    .then(async ({ guide }) => ({
      config: await loadChewConfig(),
      glossary: await loadGlossary(),
      descriptor,
      guide,
      files: await loadFixtureFiles(descriptor, guide.source),
    }))
    .catch((error: unknown) => {
      reviewBundleCache.delete(descriptor.id);
      throw error;
    });
  reviewBundleCache.set(descriptor.id, loading);
  return loading;
}
